import React from 'react';
import { clsx } from 'clsx';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { StatusBadge, PriorityBadge } from '../ui/Badge';
import { EmptyState } from '../ui/Feedback';
import { Complaint } from '../../types/complaint';

interface RecentComplaintsTableProps {
  complaints: Complaint[];
  loading?: boolean;
  error?: string | null;
  onSelect?: (complaint: Complaint) => void;
  onViewAll?: () => void;
  className?: string;
}

function formatDate(ts: string): string {
  const date = new Date(ts);
  if (isNaN(date.getTime())) return '—';
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

export const RecentComplaintsTable: React.FC<RecentComplaintsTableProps> = ({
  complaints,
  loading = false,
  error = null,
  onSelect,
  onViewAll,
  className,
}) => {
  return (
    <Card
      title="Recent Complaints"
      subtitle="Latest operational records"
      className={className}
      action={
        onViewAll ? (
          <Button variant="ghost" size="sm" onClick={onViewAll}>
            View all
          </Button>
        ) : undefined
      }
    >
      {loading ? (
        <div className="space-y-2 py-1">
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="h-9 bg-slate-800/60 rounded-lg animate-pulse" />
          ))}
        </div>
      ) : error ? (
        <p className="text-xs text-rose-400 py-4">{error}</p>
      ) : complaints.length === 0 ? (
        <EmptyState message="No complaints yet" description="New complaints will appear here." />
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-left text-xs">
            <thead className="text-slate-400">
              <tr className="border-b border-slate-800">
                <th className="py-2 pr-3 font-medium whitespace-nowrap">ID</th>
                <th className="py-2 pr-3 font-medium">Subject</th>
                <th className="py-2 pr-3 font-medium whitespace-nowrap">Department</th>
                <th className="py-2 pr-3 font-medium">Priority</th>
                <th className="py-2 pr-3 font-medium">Status</th>
                <th className="py-2 pl-3 font-medium text-right whitespace-nowrap">Date</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800/60">
              {complaints.map((c) => (
                <tr
                  key={c.complaint_id}
                  onClick={() => onSelect?.(c)}
                  className={clsx(
                    'transition-colors',
                    onSelect && 'cursor-pointer hover:bg-slate-800/40'
                  )}
                >
                  <td className="py-2.5 pr-3 font-mono text-sky-400 whitespace-nowrap">{c.complaint_id}</td>
                  <td className="py-2.5 pr-3 max-w-[240px]">
                    <span className="block truncate text-slate-100">{c.title}</span>
                  </td>
                  <td className="py-2.5 pr-3 text-slate-300 whitespace-nowrap">{c.department_name}</td>
                  <td className="py-2.5 pr-3"><PriorityBadge priority={c.priority_name} /></td>
                  <td className="py-2.5 pr-3"><StatusBadge status={c.status_name} /></td>
                  <td className="py-2.5 pl-3 text-right text-slate-400 whitespace-nowrap">{formatDate(c.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
};
